import { baseApi } from '@/Redux/baseApi';
import type { ApiScope } from '@/Pages/Integrations/apiClientsApi';

/**
 * # The whole ADR-0027 developer-portal contract, publisher's side, pinned here
 *
 * Read directly from `src/modules/devportal/` on the backend's `main` (merged,
 * production — ADR-0027, "a marketplace app is the mirror image of an
 * integration, not an instance of one"):
 *   - `developer-apps.controller.ts` for every route below
 *   - `dto/developer-app.dto.ts` for the request bodies and `DEVELOPER_APP_CATEGORIES`
 *   - `developer-apps.service.ts` for the response shapes (`APP_VIEW`,
 *     `VERSION_VIEW`, and `sandboxUsage`'s ad-hoc `{ client, days }`)
 *
 * `ApiScope` is imported from `Pages/Integrations/apiClientsApi.ts` rather than
 * redeclared — the backend does the identical thing
 * (`DeveloperAppVersion.requestedScopes` is validated against the SAME
 * `API_SCOPES` enum `ApiClient.scopes` is, in `../../public-api/scopes`), so a
 * second copy here would be exactly the kind of drift ADR-0003's own header
 * comment warns a "pin every type in one file" rule exists to prevent.
 *
 * ## The sandbox is a different TENANT, not a flag on this one
 *
 * `sandboxTenantId` is the one field on `DeveloperApp` that says so. It is
 * `null` only for the instant between "app row created" and "sandbox
 * provisioned" — `create()` provisions it in the same call — so in practice it
 * is always present. There is no route anywhere in this file that reads or
 * writes a row IN the sandbox tenant directly; `sandboxUsage` and
 * `rotateSandboxCredential` both rebind to it server-side
 * (`withTenant(app.sandboxTenantId, …)`) and hand back only what the developer
 * needs to see, never a door into it.
 */

export const DEVELOPER_APP_CATEGORIES = [
  'accounting',
  'analytics',
  'crm',
  'delivery',
  'loyalty',
  'messaging',
  'payments',
  'productivity',
  'other',
] as const;
export type DeveloperAppCategory = (typeof DEVELOPER_APP_CATEGORIES)[number];

export type DeveloperAppReviewStatus = 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';

export const DEVPORTAL_ROUTES = {
  apps: '/devportal/apps',
  app: (id: string) => `/devportal/apps/${id}`,
  sandboxUsage: (id: string) => `/devportal/apps/${id}/sandbox/usage`,
  versions: (id: string) => `/devportal/apps/${id}/versions`,
  submitVersion: (id: string) => `/devportal/apps/${id}/versions`,
  sandboxReset: (id: string) => `/devportal/apps/${id}/sandbox/reset`,
  sandboxCredentialRotate: (id: string) => `/devportal/apps/${id}/sandbox/credential/rotate`,
} as const;

/** The six keys `permissions.ts` registers under `developer_apps`. */
export const DEVPORTAL_PERMISSIONS = {
  view: 'developer_apps.view',
  create: 'developer_apps.create',
  update: 'developer_apps.update',
  delete: 'developer_apps.delete',
  submitReview: 'developer_apps.submit_review',
  credentialsRotate: 'developer_apps.credentials.rotate',
} as const;

/** `APP_VIEW` on the wire. */
export interface DeveloperApp {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: DeveloperAppCategory;
  publisherName: string;
  publisherEmail: string;
  homepageUrl: string | null;
  privacyUrl: string | null;
  supportUrl: string | null;
  logoUrl: string | null;
  status: DeveloperAppReviewStatus;
  /** The version NUMBER of the newest APPROVED version. Null until the first approval. */
  currentVersion: number | null;
  sandboxTenantId: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `VERSION_VIEW` on the wire. */
export interface DeveloperAppVersion {
  id: string;
  version: number;
  requestedScopes: ApiScope[];
  changelog: string | null;
  status: DeveloperAppReviewStatus;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export interface CreateDeveloperAppBody {
  name: string;
  description?: string;
  category: DeveloperAppCategory;
  publisherName: string;
  publisherEmail: string;
  homepageUrl?: string;
  privacyUrl?: string;
  supportUrl?: string;
  logoUrl?: string;
}

export interface UpdateDeveloperAppBody {
  name?: string;
  description?: string;
  homepageUrl?: string;
  privacyUrl?: string;
  supportUrl?: string;
  logoUrl?: string;
}

/** Submitting a version IS requesting review — there is no separate "save draft" step. */
export interface SubmitDeveloperAppVersionBody {
  requestedScopes: ApiScope[];
  changelog?: string;
}

/** Mirrors `WebhookEndpoint.rotateSecret` — default 60 on the sandbox route (unlike the plain `api-clients` rotate route, which always binds 0). */
export interface RotateSandboxCredentialBody {
  overlapMinutes?: number;
}

/** `POST .../sandbox/credential/rotate` response — either a fresh `ApiClient` or a rotated one, always with a live secret. */
export interface SandboxCredentialWithSecret {
  id: string;
  clientId: string;
  secretPrefix: string;
  scopes: ApiScope[];
  status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED';
  clientSecret: string;
}

export interface SandboxUsageDay {
  day: string;
  count: number;
}

/** `GET .../sandbox/usage` — `{ client: null, days: [] }` when no sandbox credential has ever been minted. */
export interface SandboxUsage {
  client: { clientId: string; lastUsedAt: string | null } | null;
  days: SandboxUsageDay[];
}

export const devportalApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /devportal/apps` — `developer_apps.view`. */
    developerApps: build.query<DeveloperApp[], void>({
      query: () => ({ url: DEVPORTAL_ROUTES.apps }),
      transformResponse: (response: unknown) => (Array.isArray(response) ? (response as DeveloperApp[]) : []),
      providesTags: (result) => [
        { type: 'DeveloperApp' as const, id: 'LIST' },
        ...(result ?? []).map((a) => ({ type: 'DeveloperApp' as const, id: a.id })),
      ],
    }),

    /** `GET /devportal/apps/:id` — `developer_apps.view`. */
    developerApp: build.query<DeveloperApp | null, string>({
      query: (id) => ({ url: DEVPORTAL_ROUTES.app(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as DeveloperApp) : null,
      providesTags: (_r, _e, id) => [{ type: 'DeveloperApp' as const, id }],
    }),

    /** `GET /devportal/apps/:id/versions` — `developer_apps.view`. */
    developerAppVersions: build.query<DeveloperAppVersion[], string>({
      query: (id) => ({ url: DEVPORTAL_ROUTES.versions(id) }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as DeveloperAppVersion[]) : [],
      providesTags: (_r, _e, id) => [{ type: 'DeveloperApp' as const, id: `versions:${id}` }],
    }),

    /** `GET /devportal/apps/:id/sandbox/usage` — `developer_apps.view`. Daily call counts, last 30 days. */
    developerAppSandboxUsage: build.query<SandboxUsage, string>({
      query: (id) => ({ url: DEVPORTAL_ROUTES.sandboxUsage(id) }),
      transformResponse: (response: unknown): SandboxUsage => {
        const r = response as Partial<SandboxUsage> | null;
        return { client: r?.client ?? null, days: Array.isArray(r?.days) ? r!.days : [] };
      },
      providesTags: (_r, _e, id) => [{ type: 'DeveloperApp' as const, id: `sandbox-usage:${id}` }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useDeveloperAppsQuery,
  useDeveloperAppQuery,
  useDeveloperAppVersionsQuery,
  useDeveloperAppSandboxUsageQuery,
} = devportalApi;

/** An app changed — created, edited, deleted, or a version submitted. */
export function invalidateDeveloperApp(id?: string) {
  return devportalApi.util.invalidateTags([
    { type: 'DeveloperApp' as const, id: 'LIST' },
    ...(id
      ? [
          { type: 'DeveloperApp' as const, id },
          { type: 'DeveloperApp' as const, id: `versions:${id}` },
        ]
      : []),
  ]);
}

/** The sandbox was reset or its credential rotated. */
export function invalidateSandbox(id: string) {
  return devportalApi.util.invalidateTags([{ type: 'DeveloperApp' as const, id: `sandbox-usage:${id}` }]);
}
